import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { toast } from "sonner";
import { Check, ArrowCounterClockwise, Warning } from "@phosphor-icons/react";

const TYPE_LABEL_SL = {
  UNCLOSED_ARRIVE: "Nezaprta prisotnost",
  UNCLOSED_WFH: "Nezaprto DoD",
  MISSING_DAY: "Manjkajoč delovni dan",
  OVERTIME: "Preveč ur",
  SHORT_DAY: "Prekratek dan",
};
const SEV_CSS = {
  high: "bg-[#FF3333] text-white",
  medium: "bg-[#FF8000] text-black",
  low: "bg-[#FFCC00] text-black",
};

export default function AnomaliesPage() {
  const { t } = useI18n();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [showResolved, setShowResolved] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filterType, setFilterType] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/anomalies", {
        params: { year, month, include_resolved: showResolved },
      });
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, month, showResolved]);

  const dismiss = async (a) => {
    const note = window.prompt(t("anomaly_dismiss_note") + " (" + a.user_name + " · " + a.date + ")", "");
    if (note === null) return;
    try {
      await api.post("/anomalies/dismiss", { user_id: a.user_id, date: a.date, type: a.type, note });
      toast.success(t("anomaly_dismissed"));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const reopen = async (a) => {
    try {
      await api.post("/anomalies/reopen", { user_id: a.user_id, date: a.date, type: a.type });
      toast.success(t("anomaly_reopened"));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const months = [...Array(12)].map((_, i) => i + 1);
  const years = [year - 1, year, year + 1];
  const items = (data?.anomalies || []).filter((a) => !filterType || a.type === filterType);
  const byType = useMemo(() => {
    const m = {};
    for (const a of (data?.anomalies || [])) m[a.type] = (m[a.type] || 0) + (a.resolved ? 0 : 1);
    return m;
  }, [data]);

  return (
    <div data-testid="anomalies-page">
      <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-black/60">{t("anomalies")}</div>
          <h1 className="font-display font-black text-5xl tracking-tighter leading-none flex items-center gap-3">
            <Warning size={48} weight="bold" /> {t("anomalies_title")}
          </h1>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider mb-1">{t("month")}</label>
            <select data-testid="an-month" value={month} onChange={(e) => setMonth(Number(e.target.value))} className="brut-border p-2 bg-white">
              {months.map(m => <option key={m} value={m}>{new Date(2000, m - 1, 1).toLocaleString(undefined, { month: "long" })}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider mb-1">{t("year")}</label>
            <select data-testid="an-year" value={year} onChange={(e) => setYear(Number(e.target.value))} className="brut-border p-2 bg-white">
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <label className="flex items-center gap-2 brut-border bg-white p-2 text-xs font-bold uppercase tracking-wider cursor-pointer" data-testid="an-show-resolved-wrap">
            <input
              data-testid="an-show-resolved"
              type="checkbox"
              checked={showResolved}
              onChange={(e) => setShowResolved(e.target.checked)}
            />
            {t("show_resolved")}
          </label>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        {Object.keys(TYPE_LABEL_SL).map((k) => (
          <button
            key={k}
            data-testid={`an-filter-${k}`}
            onClick={() => setFilterType(filterType === k ? "" : k)}
            className={`brut-border p-3 text-left brut-hover ${filterType === k ? "bg-black text-white" : "bg-white"}`}
          >
            <div className="text-[10px] font-bold uppercase tracking-wider opacity-70">{TYPE_LABEL_SL[k]}</div>
            <div className="font-display font-black text-3xl tracking-tighter">{byType[k] || 0}</div>
          </button>
        ))}
      </div>

      <div className="brut-border bg-white brut-shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F4F4F0] uppercase tracking-wider text-xs">
              <tr>
                <th className="text-left p-3 border-b-2 border-black">{t("date")}</th>
                <th className="text-left p-3 border-b-2 border-black">{t("employee")}</th>
                <th className="text-left p-3 border-b-2 border-black">{t("team")}</th>
                <th className="text-left p-3 border-b-2 border-black">{t("type")}</th>
                <th className="text-left p-3 border-b-2 border-black">{t("severity")}</th>
                <th className="text-left p-3 border-b-2 border-black">{t("hours")}</th>
                <th className="text-left p-3 border-b-2 border-black">{t("detail")}</th>
                <th className="text-left p-3 border-b-2 border-black">{t("actions")}</th>
              </tr>
            </thead>
            <tbody data-testid="an-table">
              {items.map((a) => (
                <tr key={`${a.user_id}-${a.date}-${a.type}`} className={`border-b border-black/10 ${a.resolved ? "opacity-50" : ""}`}>
                  <td className="p-3 font-mono">{a.date}</td>
                  <td className="p-3">
                    <div className="font-bold">{a.user_name}</div>
                    <div className="text-[10px] font-mono text-black/60">{a.employee_code || "—"}</div>
                  </td>
                  <td className="p-3">{a.team || "—"}</td>
                  <td className="p-3 font-bold">{TYPE_LABEL_SL[a.type] || a.type}</td>
                  <td className="p-3"><span className={`tag ${SEV_CSS[a.severity] || ""}`}>{a.severity}</span></td>
                  <td className="p-3 font-mono text-xs">
                    {a.worked_h ? `W:${a.worked_h}` : ""} {a.wfh_h ? ` D:${a.wfh_h}` : ""}
                  </td>
                  <td className="p-3 text-xs">{a.detail}</td>
                  <td className="p-3">
                    {a.resolved ? (
                      <button
                        data-testid={`an-reopen-${a.user_id}-${a.date}-${a.type}`}
                        onClick={() => reopen(a)}
                        className="brut-border bg-white brut-hover px-2 py-1 text-xs font-bold uppercase tracking-wider flex items-center gap-1"
                      >
                        <ArrowCounterClockwise size={14} weight="bold" /> {t("reopen")}
                      </button>
                    ) : (
                      <button
                        data-testid={`an-dismiss-${a.user_id}-${a.date}-${a.type}`}
                        onClick={() => dismiss(a)}
                        className="brut-border bg-[#00CC33] text-black brut-shadow brut-hover px-2 py-1 text-xs font-bold uppercase tracking-wider flex items-center gap-1"
                      >
                        <Check size={14} weight="bold" /> {t("dismiss")}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {!loading && items.length === 0 && (
                <tr><td colSpan={8} className="p-6 text-center text-black/60">{t("no_anomalies")}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
